import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import cn from 'classnames'
import _ from 'lodash'
import React from 'react'
import { i18n } from '../../domain/index'
import { default as MonthlyRequiresAction } from '../../domain/monthlyInfo/RequiresAction'
import { default as WeeklyRequiresAction } from '../../domain/weeklyInfo/RequiresAction'
import {
  MarketWastagesAmountAndVolumes,
  MarketWastagesAmountAndVolumesMonthly,
  MarketWastagesAmountAndVolumesWeekly,
} from '../marketSales/types'
import { formatNumber } from './marketPageUtil'
import css from './styles.css'

interface Props {
  marketWastages: (MarketWastagesAmountAndVolumesWeekly | MarketWastagesAmountAndVolumesMonthly)[]
  isMonthlyInfo?: boolean
  date: string
  stationId: string
}

const freshProductCdtLevel2Ids = ['CAAAA', 'CAAAW', 'CAAAE']

function checkFreshProduct(row: MarketWastagesAmountAndVolumes): boolean {
  return freshProductCdtLevel2Ids.some((id) => row.cdtId.startsWith(id))
}

function isWeeklyInfo(row: MarketWastagesAmountAndVolumes): row is MarketWastagesAmountAndVolumesWeekly {
  return 'wastageAmountTaxfreePreviousWeek' in row
}

function isMonthlyInfo(row: MarketWastagesAmountAndVolumes): row is MarketWastagesAmountAndVolumesMonthly {
  return 'wastageAmountTaxfreePreviousMonth' in row
}

export default function MarketWastageTable({ marketWastages, date, stationId, isMonthlyInfo = false }: Props) {
  return (
    <div className={css.salesAndHours}>
      <Paper>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell className={cn(css.tableHeading, css.arrowCell)} />
              <TableCell className={css.tableHeading}>{i18n.t('weeklyInfo.heading.productGroupName')}</TableCell>
              <TableCell className={css.tableHeading}>
                {i18n.t('weeklyInfo.heading.registeredNetWastageEuros')}
              </TableCell>
              <TableCell className={css.tableHeading}>
                {isMonthlyInfo
                  ? i18n.t('monthlyInfo.heading.registeredNetWastageEurosLastMonth')
                  : i18n.t('weeklyInfo.heading.registeredNetWastageEurosLastWeek')}
              </TableCell>
              <TableCell className={css.tableHeading}>
                {i18n.t('weeklyInfo.heading.registeredNetWastagePercentage')}
              </TableCell>
              <TableCell className={css.tableHeading}>
                {isMonthlyInfo
                  ? i18n.t('monthlyInfo.heading.registeredNetWastagePercentageLastMonth')
                  : i18n.t('weeklyInfo.heading.registeredNetWastagePercentageLastWeek')}
              </TableCell>
              <TableCell className={css.tableHeading}>
                {i18n.t('weeklyInfo.heading.registeredFreshProductWastageEuros')}
              </TableCell>
              <TableCell className={css.tableHeading}>
                {isMonthlyInfo
                  ? i18n.t('monthlyInfo.heading.registeredFreshProductWastageEurosLastMonth')
                  : i18n.t('weeklyInfo.heading.registeredFreshProductWastageEurosLastWeek')}
              </TableCell>
              <TableCell className={css.tableHeading}>
                {i18n.t('weeklyInfo.heading.registeredFreshProductWastagePercentage')}
              </TableCell>
              <TableCell className={css.tableHeading}>
                {isMonthlyInfo
                  ? i18n.t('monthlyInfo.heading.registeredFreshProductWastagePercentageLastMonth')
                  : i18n.t('weeklyInfo.heading.registeredFreshProductWastagePercentageLastWeek')}
              </TableCell>
              <TableCell className={css.tableHeading}>{i18n.t('weeklyInfo.heading.isActionRequired')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {marketWastages?.map((wastage) => (
              <MarketWastageRow key={wastage.id} row={wastage} date={date} stationId={stationId} />
            ))}
          </TableBody>
        </Table>
      </Paper>
    </div>
  )
}

function MarketWastageRow({
  row,
  date,
  stationId,
  recursionLevel = 0,
}: {
  row: MarketWastagesAmountAndVolumesWeekly | MarketWastagesAmountAndVolumesMonthly
  date: string
  stationId: string
  recursionLevel?: number
}) {
  const expandable = row.children && row.children?.length > 0

  const [isExpanded, setIsExpanded] = React.useState(false)

  const isFreshProduct = checkFreshProduct(row)

  function toggleExpanded() {
    setIsExpanded(!isExpanded)
  }

  const indentationClassName = css[`indentation${recursionLevel}`]
  return (
    <>
      <TableRow className={indentationClassName}>
        <TableCell className={cn(css.tableCell, css.arrowCell, css.pointer)}>
          {expandable && (
            <a className={cn(css.arrow, { [css.open]: isExpanded })} onClick={toggleExpanded}>
              <KeyboardArrowRightIcon />
            </a>
          )}
        </TableCell>
        <TableCell className={css.tableCell}>{row.cdtName ?? row.cdtId}</TableCell>
        <TableCell className={css.tableCell}>{!isFreshProduct ? formatNumber(row.wastageAmountTaxfree) : ''}</TableCell>
        <TableCell className={css.tableCell}>
          {!isFreshProduct
            ? formatNumber(
                isWeeklyInfo(row) ? row.wastageAmountTaxfreePreviousWeek : row.wastageAmountTaxfreePreviousMonth,
              )
            : ''}
        </TableCell>
        <ColoredCell
          value={!isFreshProduct ? row.wastagePercentage : undefined}
          low={row.wastageLowerBound}
          high={row.wastageUpperBound}
        />
        <ColoredCell
          value={
            !isFreshProduct
              ? isWeeklyInfo(row)
                ? row.wastagePercentagePreviousWeek
                : row.wastagePercentagePreviousMonth
              : undefined
          }
          low={row.wastageLowerBound}
          high={row.wastageUpperBound}
        />
        <TableCell className={css.tableCell}>{isFreshProduct ? formatNumber(row.wastageAmountTaxfree) : ''}</TableCell>
        <TableCell className={css.tableCell}>
          {isFreshProduct
            ? formatNumber(
                isWeeklyInfo(row) ? row.wastageAmountTaxfreePreviousWeek : row.wastageAmountTaxfreePreviousMonth,
              )
            : ''}
        </TableCell>
        <ColoredCell
          value={isFreshProduct ? row.wastagePercentage : undefined}
          low={row.wastageLowerBound}
          high={row.wastageUpperBound}
        />
        <ColoredCell
          value={
            isFreshProduct
              ? isWeeklyInfo(row)
                ? row.wastagePercentagePreviousWeek
                : row.wastagePercentagePreviousMonth
              : undefined
          }
          low={row.wastageLowerBound}
          high={row.wastageUpperBound}
        />
        <TableCell className={cn(css.tableCell, css.actionCell)}>
          {isMonthlyInfo(row) ? (
            <MonthlyRequiresAction
              date={date}
              isActionRequired={row.isActionRequired}
              monthlyInfoId={row.id}
              stationId={stationId}
              subject='Market'
            />
          ) : (
            <WeeklyRequiresAction
              date={date}
              isActionRequired={row.isActionRequired}
              weeklyInfoId={row.id}
              stationId={stationId}
              subject='Market'
            />
          )}
        </TableCell>
      </TableRow>
      {isExpanded &&
        row.children?.map((child) => (
          <MarketWastageRow
            key={child.id}
            row={child}
            recursionLevel={recursionLevel + 1}
            date={date}
            stationId={stationId}
          />
        ))}
    </>
  )
}

function ColoredCell({ low, high, value }: { low?: number; high?: number; value?: number }) {
  if (_.isNil(value)) {
    return <TableCell className={css.tableCell}>{''}</TableCell>
  }
  if (_.isNil(low) || _.isNil(high)) {
    return <TableCell className={css.tableCell}>{formatNumber(value)}</TableCell>
  }
  const colorStyle = value < low ? css.within : value > high ? css.above : css.below
  return <TableCell className={cn(css.tableCell, colorStyle)}>{formatNumber(value)}</TableCell>
}
